import type { DropdownOption } from 'types/ui'

/**
 * Represents a metadata filter key used in dropdowns and filtering.
 *
 * @property key - The unique key for the filter
 * @property value - The display value for the filter
 * @property filterTitle - The title used for the filter
 */
type FilterKey = {
  key: string
  value: string
  filterTitle: string
}

/**
 * Extracts the last segment of a metadata key as a display name.
 *
 * @param key - The metadata key (e.g. 'custom_field_name')
 * @returns The last segment of the key, used as a display label
 */
export function getMetadataNameForKey(key: string): string {
  const splitKey = key.split('_')
  const value = `${splitKey[splitKey.length - 1]}`
  return value
}

/**
 * Builds dropdown options from metadata keys and additional filter keys.
 *
 * Maps metadata keys and additionalKeys into a unified dropdown options array,
 * excluding any keys in the exclusionList.
 *
 * @param metadataKeys - List of metadata keys (strings or objects)
 * @param additionalKeys - List of additional filter keys
 * @param exclusionList - List of keys to exclude from the dropdown
 * @returns Array of dropdown options for UI components
 */
export function getDropdownOptions(
  // comes from reducer
  metadataKeys: Array<any>,
  // is constant
  additionalKeys: Array<FilterKey>,
  exclusionList: Array<string> = [],
): DropdownOption[] {
  const result = metadataKeys
    .map((meta: any | string) => {
      return {
        value: typeof meta === 'string' ? meta : meta.key,
        label:
          typeof meta === 'string' ? getMetadataNameForKey(meta) : meta.text,
      }
    })
    .concat(
      additionalKeys.map(({ key, value }) => ({ value: key, label: value })),
    )
    .filter(option => exclusionList.indexOf(option.value) === -1)

  return result
}

/**
 * List of additional metadata filter keys for dropdowns and filtering.
 */
export const additionalMetadataKeys: Array<FilterKey> = [
  {
    key: 'disco_comment',
    value: 'Note',
    filterTitle: 'Note',
  },
  {
    key: 'disco_internal_comment',
    value: 'Internal note',
    filterTitle: 'Internal note',
  },
  {
    key: 'disco_client_comment',
    value: 'Client note',
    filterTitle: 'Client note',
  },
]

// possibly worth merging:
// - utils/metadata
// - utils/commonMetadata

// (more to be added)
